import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, getAdminProfile } from '../../services/adminApi';
import useNoIndex from '../../hooks/useNoIndex';
import StatsBar from '../../components/admin/StatsBar';
import BookingCard from '../../components/admin/BookingCard';
import ProductsManager from '../../components/admin/ProductsManager';
import AvailabilityManager from '../../components/admin/AvailabilityManager';
import PatientsManager from '../../components/admin/PatientsManager';
import MealPlanBuilder from '../../components/admin/MealPlanBuilder';
import FoodDatabaseManager from '../../components/admin/FoodDatabaseManager';
import TimezoneSelect, { detectUserTimezone } from '../../components/TimezoneSelect';
import {
  Apple,
  BookOpen,
  Calendar,
  Clock,
  LogOut,
  Mail,
  RefreshCw,
  Users,
  Utensils,
} from 'lucide-react';

const FILTERS = ['all', 'confirmed', 'pending', 'cancelled'];

const AdminDashboard = () => {
  useNoIndex();
  const [admin, setAdmin] = useState(getAdminProfile);
  const [activeTab, setActiveTab] = useState('patients'); // 'patients' | 'mealplans' | 'foods' | 'bookings' | 'availability' | 'products' | 'messages'
  const [selectedPatientFromBooking, setSelectedPatientFromBooking] = useState(null);
  const [displayTz, setDisplayTz] = useState(
    () => localStorage.getItem('admin_display_tz') || detectUserTimezone() || 'Europe/Madrid'
  );
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('upcoming'); // 'upcoming' | 'all'
  const navigate = useNavigate();

  useEffect(() => {
    adminApi.getMe().then(setAdmin).catch(() => {});
  }, []);

  const handleTzChange = (tz) => {
    setDisplayTz(tz);
    localStorage.setItem('admin_display_tz', tz);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, c] = await Promise.all([
        adminApi.getStats(),
        view === 'upcoming'
          ? adminApi.getUpcoming()
          : adminApi.getAllBookings(filter === 'all' ? '' : filter),
        adminApi.getContacts().catch(() => []),
      ]);
      setStats(s);
      setBookings(b);
      setContacts(c);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [view, filter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleLogout = () => {
    adminApi.logout();
    navigate('/admin', { replace: true });
  };

  const handleNotesUpdate = (id, notes) => {
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, admin_notes: notes } : b)));
  };

  const handleStatusUpdate = (id, status) => {
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    if (stats) {
      load();
    }
  };

  const handleOpenPatientFromBooking = (patient) => {
    setSelectedPatientFromBooking(patient);
    setActiveTab('patients');
  };

  const groupedByDate = bookings.reduce((acc, b) => {
    const key = b.date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(b);
    return acc;
  }, {});

  const navTabs = [
    { id: 'patients', label: 'Patients (CRM)', icon: Users },
    { id: 'mealplans', label: 'Meal Plans & Templates', icon: Utensils },
    { id: 'foods', label: 'Food & Recipes DB', icon: Apple },
    { id: 'bookings', label: 'Appointments', icon: Calendar },
    { id: 'availability', label: 'Calendar & Availability', icon: Clock },
    { id: 'products', label: 'Prices & Diet eBooks', icon: BookOpen },
    { id: 'messages', label: `Messages (${contacts.length})`, icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navbar */}
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="TODOenBALANCE" className="h-8" />
            <div>
              <h1 className="font-bold text-gray-900 text-lg leading-none">
                Paola's Clinical Workspace
              </h1>
              <p className="text-xs text-gray-400">
                TODOenBALANCE · Nutritionist Practice Management
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <TimezoneSelect label="View in" value={displayTz} onChange={handleTzChange} />

            {admin && (
              <div className="hidden md:flex items-center gap-2 pr-2 border-r border-gray-200 mr-1">
                {admin.picture_url ? (
                  <img
                    src={admin.picture_url}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="w-8 h-8 rounded-full ring-2 ring-white shadow-sm"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold">
                    {(admin.name || admin.email || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="leading-tight">
                  <p className="text-sm font-semibold text-gray-800">{admin.name || 'Admin'}</p>
                  <p className="text-xs text-gray-400">{admin.email}</p>
                </div>
              </div>
            )}

            <button
              onClick={load}
              disabled={loading}
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-600 transition-colors px-3 py-2 rounded-lg hover:bg-red-50"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <StatsBar stats={stats} />

        {/* Section Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200 pb-3">
          {navTabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                activeTab === id
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'bg-white text-gray-600 hover:text-gray-900 border border-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Tab: Patients CRM & 360° Clinical Workspace */}
        {activeTab === 'patients' && (
          <PatientsManager
            initialSelectedPatient={selectedPatientFromBooking}
            onClearInitialPatient={() => setSelectedPatientFromBooking(null)}
          />
        )}

        {/* Tab: Meal Plans & Reusable Templates */}
        {activeTab === 'mealplans' && <MealPlanBuilder />}

        {/* Tab: Food & Recipes Database */}
        {activeTab === 'foods' && <FoodDatabaseManager />}

        {/* Tab: Bookings / Appointments */}
        {activeTab === 'bookings' && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex bg-white border border-gray-200 rounded-xl p-1 gap-1">
                {['upcoming', 'all'].map((v) => (
                  <button
                    key={v}
                    onClick={() => {
                      setView(v);
                      setFilter('all');
                    }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize ${
                      view === v
                        ? 'bg-primary text-white shadow-sm'
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    {v === 'upcoming' ? '📅 Upcoming 7 days' : '📋 All appointments'}
                  </button>
                ))}
              </div>

              {view === 'all' && (
                <div className="flex gap-2">
                  {FILTERS.map((f) => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                        filter === f
                          ? 'bg-primary text-white'
                          : 'bg-white border border-gray-200 text-gray-600 hover:border-primary hover:text-primary'
                      }`}
                    >
                      {f}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-20 text-gray-400">
                <RefreshCw className="w-6 h-6 animate-spin" />
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-20 text-gray-400 bg-white rounded-2xl border border-gray-100">
                <p className="text-4xl mb-3">📭</p>
                <p className="font-medium">No appointments found</p>
              </div>
            ) : view === 'upcoming' ? (
              <div className="space-y-3">
                {bookings.map((b) => (
                  <BookingCard
                    key={b.id}
                    booking={b}
                    displayTimezone={displayTz}
                    onNotesUpdate={handleNotesUpdate}
                    onStatusUpdate={handleStatusUpdate}
                    onOpenPatientFile={handleOpenPatientFromBooking}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-8">
                {Object.entries(groupedByDate)
                  .sort()
                  .map(([d, dayBookings]) => (
                    <div key={d}>
                      <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
                        {new Date(d + 'T00:00:00').toLocaleDateString('en-GB', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                        })}
                      </h3>
                      <div className="space-y-3">
                        {dayBookings.map((b) => (
                          <BookingCard
                            key={b.id}
                            booking={b}
                            displayTimezone={displayTz}
                            onNotesUpdate={handleNotesUpdate}
                            onStatusUpdate={handleStatusUpdate}
                            onOpenPatientFile={handleOpenPatientFromBooking}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </>
        )}

        {/* Tab: Availability & Calendar */}
        {activeTab === 'availability' && (
          <AvailabilityManager onBusinessTimezoneChange={handleTzChange} />
        )}

        {/* Tab: Products, Consultation Prices & Diet eBooks */}
        {activeTab === 'products' && <ProductsManager />}

        {/* Tab: Contact Form Messages */}
        {activeTab === 'messages' && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Contact Form Submissions</h2>
            {contacts.length === 0 ? (
              <p className="text-sm text-gray-400 py-10 text-center">No messages received yet.</p>
            ) : (
              <div className="space-y-3">
                {contacts.map((msg) => (
                  <div key={msg.id} className="border border-gray-200 rounded-xl p-4 space-y-1.5">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="font-bold text-gray-900">
                        {msg.name}{' '}
                        <a
                          href={`mailto:${msg.email}`}
                          className="text-sm font-normal text-primary hover:underline ml-1"
                        >
                          &lt;{msg.email}&gt;
                        </a>
                      </div>
                      <span className="text-xs text-gray-400">
                        {msg.created_at ? new Date(msg.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-line">{msg.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
